import type { View } from '../router/router';
import { apiClient } from '../services/api-client';
import { authService } from '../services/auth.service';
import { toast } from '../components/layout/toast';
import { routineStatusBadge, formatRoutineDate, type RoutineEffectiveStatus } from '../utils/routine-labels';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface Exercise {
  name: string;
  description?: string;
}

interface RoutineExercise {
  sets: number;
  reps: string;
  weight: number;
  rpe?: number;
  rir?: number;
  order: number;
  exercise: Exercise;
}

interface Routine {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate?: string | null;
  isActive: boolean;
  effectiveStatus: RoutineEffectiveStatus;
  exercises: RoutineExercise[];
}

export class RoutinesHistoryPage implements View {
  private routines: Routine[] = [];
  private loading = true;
  private errorMsg: string | null = null;

  async render(): Promise<string> {
    await this.fetchHistory();

    if (this.loading) {
      return `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 60vh; gap: 1rem;">
          <div style="color: var(--tyris-energy); animation: spin 1s linear infinite;">
            ${getTyrisIcon('en-curso', { size: 36 })}
          </div>
          <p style="color: var(--text-muted);">Cargando tu historial de rutinas...</p>
        </div>
      `;
    }

    if (this.errorMsg) {
      return `
        <div class="card-glass" style="text-align: center; padding: 3rem 1.5rem; margin-top: 2rem;">
          <div style="color: var(--color-error); margin-bottom: 1rem;">
            ${getTyrisIcon('alerta', { size: 48 })}
          </div>
          <h3 style="font-size: 1.5rem; margin-bottom: 0.5rem;">Error al recuperar datos</h3>
          <p style="color: var(--text-muted); margin-bottom: 1.5rem;">${this.errorMsg}</p>
          <button onclick="window.location.reload()" class="btn btn-primary" style="width: auto; padding: 0.75rem 2rem;">Reintentar</button>
        </div>
      `;
    }

    if (this.routines.length === 0) {
      return `
        <div class="dashboard-header">
          <div class="dashboard-title">
            <h2 style="display:flex;align-items:center;gap:0.6rem;">${getTyrisIcon('mis-rutinas', { size: 28 })} Mis Rutinas</h2>
            <p>Historial y programas de entrenamiento asignados.</p>
          </div>
        </div>
        <div class="card-glass" style="text-align: center; padding: 4rem 2rem; margin-top: 2rem; display: flex; flex-direction: column; align-items: center; justify-content: center;">
          <div style="margin-bottom: 1.5rem; color: var(--tyris-energy);">
            ${getTyrisIcon('mis-rutinas', { size: 64 })}
          </div>
          <h3 style="font-size: 1.8rem; margin-bottom: 0.75rem; color: var(--text-muted);">Sin Historial de Rutinas</h3>
          <p style="color: var(--text-muted); max-width: 460px; line-height: 1.6; margin-bottom: 2rem;">
            Aún no tenés rutinas de entrenamiento cargadas por tus entrenadores. Consultá con el profesor de turno para inicializar tu plan físico personalizado.
          </p>
        </div>
      `;
    }

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <h2 style="display:flex;align-items:center;gap:0.6rem;">${getTyrisIcon('mis-rutinas', { size: 28 })} Mis Rutinas</h2>
          <p>Tu evolución cronológica de programas de entrenamiento cargados.</p>
        </div>
      </div>

      <div style="display: flex; flex-direction: column; gap: 1.5rem;">
        ${this.routines.map((routine) => this.renderRoutineCard(routine)).join('')}
      </div>
    `;
  }

  private renderRoutineCard(routine: Routine): string {
    const badge = routineStatusBadge(routine);
    const dateStart = formatRoutineDate(routine.startDate);
    const dateEnd = routine.endDate ? formatRoutineDate(routine.endDate) : 'Sin fecha de fin';

    // Resaltar visualmente solo las rutinas vigentes
    const isEffective = routine.effectiveStatus === 'effective';
    const cardStyle = isEffective
      ? 'border-color: var(--color-primary); box-shadow: 0 0 15px var(--color-primary-glow);'
      : '';

    return `
      <div class="card-glass" style="padding: 2rem; ${cardStyle}">
        <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 1rem; margin-bottom: 1rem;">
          <div style="flex: 1; min-width: 0;">
            <h3 style="font-size: 1.4rem; font-weight: 700; margin-bottom: 0.25rem;">${routine.name}</h3>
            <p style="font-size: 0.82rem; color: var(--text-muted); font-weight: 500;">
              ${dateStart} → ${dateEnd}
            </p>
          </div>
          <span class="${badge.className}">${badge.label}</span>
        </div>

        ${routine.description ? `
          <p style="color: var(--text-muted); font-size: 0.95rem; line-height: 1.5; margin-bottom: 1.5rem;">
            ${routine.description}
          </p>
        ` : ''}

        <!-- Botón colapsable interactivo -->
        <button
          class="btn btn-outline btn-collapse-routine"
          data-target="routine-${routine.id}"
          style="justify-content: space-between; padding: 0.6rem 1.2rem; font-size: 0.88rem;"
        >
          <span style="display:inline-flex;align-items:center;gap:0.4rem;">
            ${getTyrisIcon('ver-técnica', { size: 16 })} Ver Ejercicios Planificados (${routine.exercises.length})
          </span>
          <span class="chevron" style="transition: transform 0.25s;">▼</span>
        </button>

        <!-- Tabla de Ejercicios Colapsable -->
        <div
          id="routine-${routine.id}"
          style="max-height: 0; overflow: hidden; transition: max-height 0.3s ease-out; margin-top: 1.25rem;"
        >
          <div style="background: rgba(0,0,0,0.25); border: 1px solid var(--border-metallic); border-radius: var(--radius-md); overflow-x: auto;">
            <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.9rem;">
              <thead>
                <tr style="border-bottom: 1px solid var(--border-metallic); background: rgba(255,255,255,0.02);">
                  <th style="padding: 0.85rem 1rem; color: var(--text-muted); font-weight: 600;">Orden</th>
                  <th style="padding: 0.85rem 1rem; color: var(--text-muted); font-weight: 600;">Ejercicio</th>
                  <th style="padding: 0.85rem 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Sets</th>
                  <th style="padding: 0.85rem 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Reps</th>
                  <th style="padding: 0.85rem 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">Peso</th>
                  <th style="padding: 0.85rem 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">RPE</th>
                  <th style="padding: 0.85rem 1rem; color: var(--text-muted); font-weight: 600; text-align: center;">RIR</th>
                </tr>
              </thead>
              <tbody>
                ${[...routine.exercises].sort((a, b) => a.order - b.order).map((item) => `
                  <tr style="border-bottom: 1px solid rgba(255,255,255,0.03);">
                    <td style="padding: 0.75rem 1rem; font-weight: 700; color: var(--color-primary);">${item.order}</td>
                    <td style="padding: 0.75rem 1rem;">
                      <div style="font-weight: 600;">${item.exercise.name}</div>
                    </td>
                    <td style="padding: 0.75rem 1rem; text-align: center; color: var(--color-primary); font-weight: 600;">${item.sets}</td>
                    <td style="padding: 0.75rem 1rem; text-align: center; font-weight: 600;">${item.reps}</td>
                    <td style="padding: 0.75rem 1rem; text-align: center; color: var(--color-secondary); font-weight: 600;">${item.weight} kg</td>
                    <td style="padding: 0.75rem 1rem; text-align: center; font-weight: 600;">${item.rpe ? `@${item.rpe}` : '-'}</td>
                    <td style="padding: 0.75rem 1rem; text-align: center; font-weight: 600;">${item.rir != null ? item.rir + ' RIR' : '-'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  afterRender(): void {
    const collapseBtns = document.querySelectorAll('.btn-collapse-routine');
    collapseBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.getAttribute('data-target');
        if (!targetId) return;
        const targetEl = document.getElementById(targetId);
        const chevron = btn.querySelector('.chevron') as HTMLElement | null;
        if (targetEl) {
          const isOpen = targetEl.style.maxHeight && targetEl.style.maxHeight !== '0px';
          if (!isOpen) {
            targetEl.style.maxHeight = `${targetEl.scrollHeight + 40}px`;
            (btn as HTMLElement).style.borderColor = 'var(--text-muted)';
            if (chevron) chevron.style.transform = 'rotate(180deg)';
          } else {
            targetEl.style.maxHeight = '0px';
            (btn as HTMLElement).style.borderColor = 'var(--border-metallic)';
            if (chevron) chevron.style.transform = 'rotate(0deg)';
          }
        }
      });
    });
  }

  private async fetchHistory(): Promise<void> {
    try {
      const user = authService.getUser();
      if (user?.id) {
        this.routines = await apiClient.get<Routine[]>(`/api/routines/student/${user.id}`);
      } else {
        this.routines = [];
      }
      this.errorMsg = null;
    } catch (err: any) {
      console.error('Error al recuperar historial de rutinas:', err);
      this.errorMsg = err.message || 'No se pudo conectar al servidor.';
      toast.error('Error al sincronizar tu historial de rutinas.');
    } finally {
      this.loading = false;
    }
  }
}
